/**
 * UploadDialog — Modal for uploading a single document with metadata
 *
 * Extracted from library/docs.tsx for reuse across Documents and Library pages.
 * Supports drag-and-drop, file selection, title, type, description, classification.
 */

import { useEffect, useRef, useState } from 'react';
import { FileText, Upload, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { uploadFile } from '@/lib/upload-utils';
import { useLibraryFolders } from '@/lib/use-library-folders';
import { useSysLibFolders } from '@/lib/use-sysadmin-library';
import { FolderListError, FolderLifecycleNote } from '@/components/library/folder-picker-states';
import {
  appendClassification,
  invalidateClassificationPolicy,
  useClassificationPolicy,
  type ClassificationControlsView,
} from '@/lib/use-classification-policy';
import { ClassificationPolicyError } from '@/components/library/classification-policy-error';
import { DocumentTypeSelect } from '@/components/library/document-type-picker';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface UploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploaded: () => void;
  defaultFolderId?: string | null;
  /**
   * Optional override for the upload endpoint. Used by sysadmin
   * Library to target /admin/platform/library/documents/upload while
   * keeping the tenant default at /knowledge/documents/upload. When
   * omitted, behaves exactly as before.
   *
   * The endpoint must accept the same multipart fields:
   *   file, title, documentType, description?, sensitivity?,
   *   exportControl?, folderId? — the two classification fields only
   *   when their controls are shown (LB6-T7).
   */
  uploadEndpoint?: string;
  /**
   * Custom upload function — overrides the default `uploadFile()`.
   * When provided, takes precedence over `uploadEndpoint`. Used by
   * surfaces that want to drive the upload through a React Query
   * mutation (so the surrounding cache invalidates automatically).
   */
  uploader?: (formData: FormData) => Promise<unknown>;
  /** Dialog title (default: "Upload Document"). */
  title?: string;
  /** Dialog body description (default: tenant-flavoured copy). */
  description?: string;
  /**
   * Whose folders the picker lists (L18-11). 'tenant' (default): the
   * tenant's Library folders (lib/use-library-folders.ts). 'system': the
   * System Library's folders (GET /admin/platform/library/folders) — the
   * sysadmin upload endpoint accepts only those.
   */
  folderScope?: 'tenant' | 'system';
}

/**
 * The System Library (folderScope 'system', the sysadmin upload) has no
 * tenant and so no compliance frameworks: its classification is the
 * platform's curation, and its upload route takes both fields as before.
 */
const SYSTEM_LIBRARY_CONTROLS: ClassificationControlsView = { sensitivity: true, exportControl: true, any: true };

/**
 * The multipart body of an upload. The classification fields go in ONLY for
 * the controls shown (LB6-T7): a hidden control sends no key — not its
 * default — so the api never sees a classification the tenant cannot set.
 */
export function buildUploadFormData(
  file: File,
  values: {
    title: string;
    documentType: string;
    description: string;
    folderId: string | null;
    sensitivity: string;
    exportControl: string;
  },
  controls: ClassificationControlsView,
): FormData {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('title', values.title || file.name.replace(/\.[^.]+$/, ''));
  formData.append('documentType', values.documentType);
  if (values.description.trim()) formData.append('description', values.description.trim());
  appendClassification(formData, controls, { sensitivity: values.sensitivity, exportControl: values.exportControl });
  // #140 — explicit picker value takes precedence over the default.
  if (values.folderId) formData.append('folderId', values.folderId);
  return formData;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / 1048576).toFixed(1) + ' MB';
}

export function UploadDialog({
  open,
  onOpenChange,
  onUploaded,
  defaultFolderId,
  uploadEndpoint,
  uploader,
  title: dialogTitle,
  description: dialogDescription,
  folderScope = 'tenant',
}: UploadDialogProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState('other');
  const [description, setDescription] = useState('');
  const [sensitivity, setSensitivity] = useState('unclassified');
  const [exportControl, setExportControl] = useState('none');
  // #140 — Folder is now an explicit picker rather than an implicit
  // prop. We default to whatever the caller passed in (so opening the
  // dialog from inside a folder pre-selects it) but the user can pick
  // any folder OR "No folder (root)" before uploading.
  const [folderId, setFolderId] = useState<string | null>(defaultFolderId ?? null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // #140 / decision 18 — the picker lists the ONE folder set of the scope
  // the upload lands in, read while the dialog is open (both queries refetch
  // when they become enabled, so a folder created elsewhere shows up). The
  // picker stays non-blocking: if the list fails, the upload can still go to
  // the root or `defaultFolderId` — and the dialog SAYS so (LB2-T3-R3 /
  // LB2T3-BF-04): a failed read is an error and the picker is disabled, never
  // an enabled list holding only the root.
  const tenantFoldersQ = useLibraryFolders({ enabled: open && folderScope === 'tenant' });
  const systemFoldersQ = useSysLibFolders(undefined, { enabled: open && folderScope === 'system' });
  const folders: Array<{ id: string; name: string; status?: string }> =
    folderScope === 'system' ? systemFoldersQ.data?.items ?? [] : tenantFoldersQ.data ?? [];
  const foldersLoading =
    folderScope === 'system' ? systemFoldersQ.isLoading : tenantFoldersQ.isLoading;
  const foldersError =
    folderScope === 'system'
      ? systemFoldersQ.isError && !systemFoldersQ.data
      : tenantFoldersQ.isError && !tenantFoldersQ.data;
  const retryFolders = () =>
    void (folderScope === 'system' ? systemFoldersQ.refetch() : tenantFoldersQ.refetch());

  // Decision 28 (B7, LB6-T7): a tenant upload shows — and SENDS — only the
  // classification controls the tenant's compliance frameworks show; no
  // answer or a failed one shows and sends neither (the api refuses a hidden
  // key with 400 FRAMEWORK_NOT_ACTIVE).
  const tenantPolicy = useClassificationPolicy({ enabled: open && folderScope === 'tenant' });
  const controls = folderScope === 'system' ? SYSTEM_LIBRARY_CONTROLS : tenantPolicy.controls;
  // Review LB6T7-BF-01: a tenant upload is sent only on a CURRENT good policy
  // answer — not knowing is not "hidden" (hidden would store it unclassified).
  const policyReady = folderScope === 'system' || tenantPolicy.ready;
  const policyUnavailable = folderScope === 'tenant' && tenantPolicy.unavailable;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!open) return;
    setFolderId(defaultFolderId ?? null);
  }, [open, defaultFolderId]);

  const resetForm = () => {
    setSelectedFile(null);
    setTitle('');
    setDocType('other');
    setDescription('');
    setSensitivity('unclassified');
    setExportControl('none');
    setFolderId(defaultFolderId ?? null);
  };

  const handleFileSelect = (files: FileList | null) => {
    if (files?.[0]) {
      const file = files[0];
      setSelectedFile(file);
      if (!title) setTitle(file.name.replace(/\.[^.]+$/, ''));
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !policyReady) return;
    setUploading(true);
    const formData = buildUploadFormData(
      selectedFile,
      { title, documentType: docType, description, folderId, sensitivity, exportControl },
      controls,
    );
    try {
      if (uploader) {
        await uploader(formData);
      } else {
        await uploadFile(uploadEndpoint ?? '/knowledge/documents/upload', formData);
      }
      toast.success(`Uploaded: ${selectedFile.name}`);
      onOpenChange(false);
      resetForm();
      onUploaded();
    } catch (err) {
      // Surface the real backend message when we have one (PR #327
      // pattern). The previous `catch {}` swallowed every API
      // error code and showed "Upload failed", which prevented the
      // user from learning whether it was an oversize file vs an
      // invalid folder vs a 429.
      const msg = err instanceof Error ? err.message : 'Upload failed';
      toast.error(msg);
      // A framework turned on or off since the dialog opened (e.g. 400
      // FRAMEWORK_NOT_ACTIVE): re-ask, so the controls shown are the tenant's
      // now (review LB6T7-BF-06).
      if (folderScope === 'tenant') void invalidateClassificationPolicy(queryClient);
    }
    setUploading(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetForm(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{dialogTitle ?? 'Upload Document'}</DialogTitle>
          {/* #226 — explicit DialogDescription for screen-reader
              context + to silence the Radix accessibility warning. */}
          <DialogDescription>
            {dialogDescription ??
              'Add a single file to the Library. RAG-indexed formats are searchable by Ask Argus once ingestion completes; image and CAD formats are stored as files only (text extraction requires the Docling service).'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div
            className={cn(
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
              dragActive ? 'border-primary bg-primary/5' : 'hover:border-primary/50',
            )}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
            onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
            onDrop={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false); handleFileSelect(e.dataTransfer.files); }}
          >
            {selectedFile ? (
              <div>
                <FileText className="h-8 w-8 mx-auto mb-2 text-primary" />
                <p className="text-sm font-medium">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{formatSize(selectedFile.size)}</p>
                <p className="text-xs text-primary mt-2 underline">Click to change file</p>
              </div>
            ) : (
              // #227 — honest two-tier accepted-types copy. The
              // backend already accepts image + CAD extensions
              // (knowledge.ts:ALLOWED_EXTENSIONS), but text
              // extraction for those requires the optional Docling
              // service. Surface that distinction up front rather
              // than letting a user upload a PNG and silently get
              // garbage chunks.
              <div>
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm font-medium">Drag and drop a file here, or click to select</p>
                <p className="text-xs text-muted-foreground mt-1">
                  <strong>RAG-indexed:</strong> PDF, DOCX, XLSX, PPTX, TXT, MD, CSV, JSON, XML, YAML
                </p>
                <p className="text-[10px] text-muted-foreground/80 mt-0.5">
                  <strong>Stored only (no text extraction yet):</strong> PNG, JPG, GIF, SVG, WEBP, BMP, DWG, DXF
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.xlsx,.pptx,.txt,.md,.csv,.json,.xml,.yaml,.yml,.png,.jpg,.jpeg,.gif,.svg,.webp,.bmp,.dwg,.dxf"
              className="hidden"
              onChange={e => handleFileSelect(e.target.files)}
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Title</Label>
            <Input value={title} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)} placeholder="e.g., Pump P-401 Service Manual" />
          </div>

          {/* LB6-T10 (decision 40) — the options are THE catalogue: the
              built-ins and the tenant's own types (the System Library's
              upload: the built-ins), read through lib/use-document-types.ts. */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Document Type</Label>
            <DocumentTypeSelect
              value={docType}
              onValueChange={setDocType}
              scope={folderScope === 'system' ? 'system' : 'tenant'}
              enabled={open}
              errorConsequence="The file will be uploaded with the default type."
            />
          </div>

          {/* #140 — Folder picker. Without this, the only way to land a
              document inside a folder was to open the dialog while
              already inside that folder, which made organization
              implicit + un-reviewable. Now the user always sees and
              can change the destination. */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Folder <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Select
              value={folderId ?? '__root__'}
              onValueChange={(v) => setFolderId(v === '__root__' ? null : v)}
              disabled={foldersLoading || foldersError}
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder={foldersLoading ? 'Loading folders…' : 'No folder (root)'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__root__">No folder (root)</SelectItem>
                {folders.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name}
                    {/* LB2T3-BF-07 — a System Library folder that is not published says so. */}
                    <FolderLifecycleNote status={f.status} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {foldersError ? (
              <FolderListError
                consequence={
                  folderId
                    ? 'The file will be uploaded into the folder this dialog was opened from.'
                    : 'The file will be uploaded to the root.'
                }
                onRetry={retryFolders}
              />
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description of what this document contains" rows={3} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" />
          </div>

          {controls.any ? (
          <div className="rounded-md border border-muted p-3 space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Classification</p>
            <div className="grid grid-cols-2 gap-3">
              {controls.sensitivity ? (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Sensitivity</Label>
                <Select value={sensitivity} onValueChange={setSensitivity}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unclassified">Unclassified</SelectItem>
                    <SelectItem value="cui">CUI</SelectItem>
                    <SelectItem value="restricted">Restricted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              ) : null}
              {controls.exportControl ? (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Export Control</Label>
                <Select value={exportControl} onValueChange={setExportControl}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="itar">ITAR</SelectItem>
                    <SelectItem value="ear">EAR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              ) : null}
            </div>
          </div>
          ) : null}
          {policyUnavailable ? <ClassificationPolicyError onRetry={tenantPolicy.retry} /> : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { resetForm(); onOpenChange(false); }}>Cancel</Button>
          <Button onClick={handleUpload} disabled={!selectedFile || uploading || !policyReady}>
            {uploading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
